import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiClient, API_ENDPOINTS } from '../../config/api';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Gavel,
  Visibility,
  AssignmentInd,
  CheckCircle,
  Cancel,
  InfoOutlined,
  Schedule,
  Warning,
  Replay,
} from '@mui/icons-material';

// ─── Design tokens ─────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── Types ─────────────────────────────────────────────────────────
interface AttachmentItem {
  id: number;
  storageKey: string;
  mimeType: string;
  fileSize: number;
  downloadUrl: string;
}

interface AppealAdminListItem {
  id: number;
  userId: number;
  sanctionId: number;
  sanctionTypeKey: string;
  sanctionTypeTitle: string;
  statusKey: string;
  statusTitle: string;
  adminId?: number;
  isReappeal: boolean;
  deadline: string;
  createdTime: string;
}

interface AppealAdminDetail extends AppealAdminListItem {
  reason: string;
  reviewResultKey?: string;
  reviewResultTitle?: string;
  reviewReason?: string;
  compensation?: string;
  reviewedAt?: string;
  updatedTime?: string;
}

interface PageResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

// ─── Helpers ───────────────────────────────────────────────────────
const formatDateTime = (s: string) =>
  new Date(s).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const formatRelative = (s: string) => {
  const diff = Date.now() - new Date(s).getTime();
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (d > 0) return `${d}일 전`;
  if (h > 0) return `${h}시간 전`;
  return '방금 전';
};

const formatDeadline = (s: string) => {
  const diff = new Date(s).getTime() - Date.now();
  const d = Math.ceil(diff / 86_400_000);
  if (d < 0) return '기한 초과';
  if (d === 0) return '오늘 마감';
  return `${d}일 남음`;
};

// ─── Config ────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string; icon: React.ReactNode }> = {
  SUBMITTED:    { label: '접수됨',   color: '#E65100', bg: '#FFF3E0', dot: '#FF6D00', icon: <Warning sx={{ fontSize: 13 }} /> },
  UNDER_REVIEW: { label: '검토중',   color: '#1565C0', bg: '#E3F2FD', dot: '#1976D2', icon: <Schedule sx={{ fontSize: 13 }} /> },
  ACCEPTED:     { label: '인정됨',   color: '#2E7D32', bg: '#E8F5E9', dot: '#43A047', icon: <CheckCircle sx={{ fontSize: 13 }} /> },
  REJECTED:     { label: '기각됨',   color: '#C62828', bg: '#FFEBEE', dot: '#EF5350', icon: <Cancel sx={{ fontSize: 13 }} /> },
  CLOSED:       { label: '종결됨',   color: '#37474F', bg: '#ECEFF1', dot: '#607D8B', icon: <CheckCircle sx={{ fontSize: 13 }} /> },
};

const SANCTION_CONFIG: Record<string, { label: string; color: string }> = {
  WARNING:    { label: '경고',     color: '#F57C00' },
  SUSPENSION: { label: '일시정지', color: '#C62828' },
  BAN:        { label: '영구정지', color: '#AD1457' },
};

// ─── Sub-component: StatCard ────────────────────────────────────────
interface StatCardProps { icon: React.ReactNode; value: number; label: string; accent: string; pulse?: boolean; }
const StatCard: React.FC<StatCardProps> = ({ icon, value, label, accent, pulse }) => (
  <Card elevation={0} sx={{
    border: '1px solid #F0E8F5', borderLeft: `4px solid ${accent}`, borderRadius: '14px',
    p: 2.5, display: 'flex', alignItems: 'center', gap: 2,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
  }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '12px', background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent, flexShrink: 0, position: 'relative' }}>
      {icon}
      {pulse && value > 0 && <Box sx={{ position: 'absolute', top: -4, right: -4, width: 12, height: 12, borderRadius: '50%', background: accent, animation: 'pulse 1.5s ease-in-out infinite' }} />}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: accent, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: '#888', fontWeight: 500, mt: 0.25 }}>{label}</Typography>
    </Box>
  </Card>
);

// ─── Main component ─────────────────────────────────────────────────
const AdminAppealsPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [appeals, setAppeals]             = useState<AppealAdminListItem[]>([]);
  const [selected, setSelected]           = useState<AppealAdminDetail | null>(null);
  const [detailDialog, setDetailDialog]   = useState(false);
  const [reviewDialog, setReviewDialog]   = useState(false);
  const [reviewResult, setReviewResult]   = useState<'ACCEPTED' | 'REJECTED'>('ACCEPTED');
  const [reviewReason, setReviewReason]   = useState('');
  const [compensation, setCompensation]   = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterSanction, setFilterSanction] = useState('');
  const [page, setPage]                   = useState(0);
  const [totalPages, setTotalPages]       = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading]             = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [attachments, setAttachments]     = useState<AttachmentItem[]>([]);
  const [snack, setSnack]                 = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });

  const adminId = Number((user as any)?.userId ?? user?.id) || 0;

  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  const showSnack = (msg: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, msg, severity });

  const fetchAppeals = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), size: '20' });
      if (filterStatus)  qs.append('status', filterStatus);
      if (filterSanction) qs.append('sanctionType', filterSanction);
      const res = await apiClient.get<{ resultType: string; result: PageResponse<AppealAdminListItem> }>(
        `${API_ENDPOINTS.ADMIN.APPEALS.LIST}?${qs.toString()}`
      );
      setAppeals(res.result.content);
      setTotalPages(res.result.totalPages);
      setTotalElements(res.result.totalElements);
    } catch {
      showSnack('목록 조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, filterSanction]);

  useEffect(() => {
    if (!isLoading && user?.role === 'ADMIN') fetchAppeals();
  }, [fetchAppeals, isLoading, user]);

  const openDetail = async (item: AppealAdminListItem) => {
    setDetailLoading(true);
    setDetailDialog(true);
    setAttachments([]);
    try {
      const res = await apiClient.get<{ resultType: string; result: AppealAdminDetail }>(
        API_ENDPOINTS.ADMIN.APPEALS.DETAIL(item.id)
      );
      setSelected(res.result);
      try {
        const attRes = await apiClient.get<{ resultType: string; result: { attachments: AttachmentItem[] } }>(
          `/api/v1/gth-common/silver-connect/appeals/${item.id}/attachments`
        );
        setAttachments(attRes.result.attachments);
      } catch {
        // attachment fetch failure does not block detail display
      }
    } catch {
      showSnack('상세 조회에 실패했습니다.', 'error');
      setDetailDialog(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAssign = async (id: number) => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.APPEALS.ASSIGN(id), { adminId });
      showSnack('배정되었습니다.');
      fetchAppeals();
    } catch {
      showSnack('배정에 실패했습니다.', 'error');
    }
  };

  const handleReview = async () => {
    if (!selected || !reviewReason.trim()) return;
    setActionLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.APPEALS.REVIEW(selected.id), {
        adminId,
        result: reviewResult,
        reviewReason,
        ...(reviewResult === 'ACCEPTED' && compensation ? { compensation } : {}),
      });
      showSnack(reviewResult === 'ACCEPTED' ? '이의제기가 인정되었습니다.' : '이의제기가 기각되었습니다.');
      setReviewDialog(false);
      setDetailDialog(false);
      setReviewReason('');
      setCompensation('');
      fetchAppeals();
    } catch {
      showSnack('심사 처리에 실패했습니다.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const stats = {
    total:       totalElements,
    submitted:   appeals.filter(a => a.statusKey === 'SUBMITTED').length,
    underReview: appeals.filter(a => a.statusKey === 'UNDER_REVIEW').length,
    accepted:    appeals.filter(a => a.statusKey === 'ACCEPTED').length,
  };

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  return (
    <AdminLayout>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(1.3)} }
        @keyframes rowIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
      `}</style>

      <Box sx={{ p: { xs: 2, md: 4 }, background: '#F8F4F9', minHeight: '100vh' }}>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ color: PINK, fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem' }}>
            ADMIN · APPEALS
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" sx={{
              fontWeight: 900, mt: 0.5,
              background: `linear-gradient(135deg, #1A0A2E 30%, ${CORAL})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              이의제기 관리
            </Typography>
            {stats.submitted > 0 && (
              <Chip label={`대기 ${stats.submitted}건`} size="small"
                sx={{ background: '#FF6D00', color: '#fff', fontWeight: 700, fontSize: '0.7rem', animation: 'pulse 2s ease-in-out infinite' }} />
            )}
          </Box>
          <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 0.5 }}>
            제재에 대한 이의제기 접수 및 심사 관리
          </Typography>
        </Box>

        {/* Stat cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
          <StatCard icon={<Gavel sx={{ fontSize: 24 }} />}        value={stats.total}       label="전체 이의제기" accent="#7B1FA2" />
          <StatCard icon={<Warning sx={{ fontSize: 24 }} />}       value={stats.submitted}   label="접수 대기"     accent="#E65100" pulse />
          <StatCard icon={<Schedule sx={{ fontSize: 24 }} />}      value={stats.underReview} label="검토중"        accent="#1565C0" />
          <StatCard icon={<CheckCircle sx={{ fontSize: 24 }} />}   value={stats.accepted}    label="인정 완료"     accent="#2E7D32" />
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>상태</InputLabel>
            <Select value={filterStatus} label="상태" onChange={e => { setFilterStatus(e.target.value); setPage(0); }} sx={{ borderRadius: '10px' }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="SUBMITTED">접수됨</MenuItem>
              <MenuItem value="UNDER_REVIEW">검토중</MenuItem>
              <MenuItem value="ACCEPTED">인정됨</MenuItem>
              <MenuItem value="REJECTED">기각됨</MenuItem>
              <MenuItem value="CLOSED">종결됨</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>제재 유형</InputLabel>
            <Select value={filterSanction} label="제재 유형" onChange={e => { setFilterSanction(e.target.value); setPage(0); }} sx={{ borderRadius: '10px' }}>
              <MenuItem value="">전체</MenuItem>
              <MenuItem value="WARNING">경고</MenuItem>
              <MenuItem value="SUSPENSION">일시정지</MenuItem>
              <MenuItem value="BAN">영구정지</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <Card elevation={0} sx={{ borderRadius: '20px', border: '1px solid #F0E8F5', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: PINK }} /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(90deg, #1A0A2E 0%, #4A1265 100%)' }}>
                    {['ID', '제재 유형', '재이의여부', '마감일', '접수 시각', '상태', '담당자', '처리'].map(h => (
                      <TableCell key={h} sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: '0.75rem', py: 1.8, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appeals.map((item, idx) => {
                    const statusCfg  = STATUS_CONFIG[item.statusKey]   ?? STATUS_CONFIG['SUBMITTED'];
                    const sanctionCfg = SANCTION_CONFIG[item.sanctionTypeKey] ?? { label: item.sanctionTypeTitle, color: '#555' };
                    const isPending  = item.statusKey === 'SUBMITTED';
                    const deadlineStr = formatDeadline(item.deadline);
                    const isOverdue  = deadlineStr === '기한 초과';
                    return (
                      <TableRow key={item.id} onClick={() => openDetail(item)} sx={{
                        animation: `rowIn 0.3s ease ${idx * 0.04}s both`,
                        borderLeft: isPending ? '4px solid #FF6D00' : '4px solid transparent',
                        background: isPending ? '#FFF8F0' : '#fff',
                        '&:hover': { background: isPending ? '#FFF0E0' : '#FAF5FF' },
                        '& td': { borderBottom: '1px solid #F3EBF8' },
                        cursor: 'pointer',
                      }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Typography sx={{ fontWeight: 700, color: '#555', fontSize: '0.85rem' }}>#{item.id}</Typography>
                            {item.isReappeal && (
                              <Tooltip title="재이의제기">
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', background: '#F3E5F5', color: '#7B1FA2', px: 0.7, py: 0.1, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 800 }}>
                                  <Replay sx={{ fontSize: 10, mr: 0.3 }} />재
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={sanctionCfg.label} size="small"
                            sx={{ background: `${sanctionCfg.color}15`, color: sanctionCfg.color, border: `1px solid ${sanctionCfg.color}30`, fontWeight: 700, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: item.isReappeal ? '#7B1FA2' : '#888', fontWeight: item.isReappeal ? 700 : 400 }}>
                            {item.isReappeal ? '재이의제기' : '최초'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isOverdue ? '#C62828' : '#555', whiteSpace: 'nowrap' }}>
                            {deadlineStr}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#BBB' }}>{formatDateTime(item.deadline)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: '#888', whiteSpace: 'nowrap' }}>{formatRelative(item.createdTime)}</Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#BBB' }}>{formatDateTime(item.createdTime)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.color}33`, px: 1.2, py: 0.4, borderRadius: '100px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: statusCfg.dot, ...(isPending && { animation: 'pulse 1.5s ease-in-out infinite' }) }} />
                            {statusCfg.label}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.8rem', color: item.adminId ? '#333' : '#BBB' }}>
                            {item.adminId ? `#${item.adminId}` : '미배정'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="상세 보기">
                              <IconButton size="small" onClick={() => openDetail(item)} sx={{ color: '#9C27B0', '&:hover': { background: '#F3E5F5' } }}>
                                <Visibility sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            {!item.adminId && (
                              <Tooltip title="배정">
                                <IconButton size="small" onClick={() => handleAssign(item.id)} sx={{ color: '#1976D2', '&:hover': { background: '#E3F2FD' } }}>
                                  <AssignmentInd sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(item.statusKey === 'SUBMITTED' || item.statusKey === 'UNDER_REVIEW') && (
                              <Tooltip title="심사">
                                <IconButton size="small" onClick={() => { openDetail(item); setTimeout(() => setReviewDialog(true), 300); }} sx={{ color: '#2E7D32', '&:hover': { background: '#E8F5E9' } }}>
                                  <Gavel sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {appeals.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <InfoOutlined sx={{ fontSize: 40, color: '#DDD', mb: 1 }} />
                        <Typography sx={{ color: '#AAA' }}>이의제기 내역이 없습니다.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination count={totalPages} page={page + 1} onChange={(_, v) => setPage(v - 1)} color="primary" sx={{ '& .MuiPaginationItem-root': { borderRadius: '8px' } }} />
          </Box>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: PINK }} /></Box>
          ) : selected && (() => {
            const cfg = STATUS_CONFIG[selected.statusKey] ?? STATUS_CONFIG['SUBMITTED'];
            const sanctionCfg = SANCTION_CONFIG[selected.sanctionTypeKey] ?? { label: selected.sanctionTypeTitle, color: '#555' };
            return (
              <>
                <DialogTitle sx={{ p: 0 }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #1A0A2E 0%, #4A1265 100%)', p: 3, color: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: `${sanctionCfg.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                        <Gavel />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>이의제기 #{selected.id}</Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{formatDateTime(selected.createdTime)} 접수</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3, pt: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#1976D208', border: '1px solid #1976D220' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>사용자 ID</Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1976D2' }}>#{selected.userId}</Typography>
                      </Box>
                      <Box sx={{ p: 2, borderRadius: '12px', background: `${sanctionCfg.color}08`, border: `1px solid ${sanctionCfg.color}20` }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>제재 유형</Typography>
                        <Chip label={sanctionCfg.label} size="small" sx={{ background: `${sanctionCfg.color}15`, color: sanctionCfg.color, fontWeight: 700 }} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>마감일</Typography>
                        <Typography sx={{ fontWeight: 700, color: formatDeadline(selected.deadline) === '기한 초과' ? '#C62828' : '#333', fontSize: '0.85rem' }}>
                          {formatDeadline(selected.deadline)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#AAA' }}>{formatDateTime(selected.deadline)}</Typography>
                      </Box>
                      <Box sx={{ p: 2, borderRadius: '12px', background: selected.isReappeal ? '#F3E5F5' : '#F8F4F9', border: `1px solid ${selected.isReappeal ? '#CE93D8' : '#EDE0F5'}` }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 0.5 }}>구분</Typography>
                        <Typography sx={{ fontWeight: 700, color: selected.isReappeal ? '#7B1FA2' : '#555' }}>
                          {selected.isReappeal ? '재이의제기' : '최초 이의제기'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 1 }}>이의제기 사유</Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>{selected.reason}</Typography>
                    </Box>
                    {selected.reviewReason && (
                      <Box sx={{ p: 2, borderRadius: '12px', background: selected.reviewResultKey === 'ACCEPTED' ? '#E8F5E9' : '#FFEBEE', border: `1px solid ${selected.reviewResultKey === 'ACCEPTED' ? '#A5D6A7' : '#EF9A9A'}` }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 700, mb: 0.5 }}>심사 결과: {selected.reviewResultTitle}</Typography>
                        <Typography sx={{ fontSize: '0.85rem', color: '#333', lineHeight: 1.5 }}>{selected.reviewReason}</Typography>
                        {selected.compensation && (
                          <Box sx={{ mt: 1, p: 1.5, borderRadius: '8px', background: '#FFF9C4', border: '1px solid #FDD835' }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#F57F17', fontWeight: 700 }}>보상 내용</Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: '#333' }}>{selected.compensation}</Typography>
                          </Box>
                        )}
                        {selected.reviewedAt && (
                          <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 1 }}>{formatDateTime(selected.reviewedAt)}</Typography>
                        )}
                      </Box>
                    )}
                    {attachments.length > 0 && (
                      <Box sx={{ p: 2, borderRadius: '12px', background: '#F8F4F9', border: '1px solid #EDE0F5' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600, mb: 1 }}>
                          첨부파일 ({attachments.length}개)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {attachments.map((att) => (
                            <Box
                              key={att.id}
                              component="a"
                              href={att.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                width: 80, height: 80, borderRadius: '8px', overflow: 'hidden',
                                border: '1px solid #E0E0E0', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                textDecoration: 'none',
                                '&:hover': { borderColor: '#9C27B0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                              }}
                            >
                              {att.mimeType.startsWith('image/') ? (
                                <img src={att.downloadUrl} alt="첨부파일" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Box sx={{ textAlign: 'center', p: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.6rem', color: '#666', wordBreak: 'break-all' }}>
                                    {att.mimeType === 'application/pdf' ? 'PDF' : 'FILE'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.55rem', color: '#999' }}>
                                    {(att.fileSize / 1024).toFixed(0)}KB
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ p: 2, borderRadius: '12px', background: `${cfg.color}08`, border: `1px solid ${cfg.color}25`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#999', fontWeight: 600 }}>처리 상태</Typography>
                        <Typography sx={{ fontWeight: 700, color: cfg.color, fontSize: '0.9rem' }}>{cfg.label}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                  {(selected.statusKey === 'SUBMITTED' || selected.statusKey === 'UNDER_REVIEW') && (
                    <Button variant="contained" onClick={() => setReviewDialog(true)}
                      sx={{ borderRadius: '10px', background: PINK, textTransform: 'none', fontWeight: 700, '&:hover': { background: '#D1366E' } }}
                      startIcon={<Gavel sx={{ fontSize: 16 }} />}>
                      심사하기
                    </Button>
                  )}
                  <Button onClick={() => setDetailDialog(false)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>닫기</Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ p: 0 }}>
            <Box sx={{
              background: reviewResult === 'ACCEPTED' ? 'linear-gradient(135deg, #1B5E20, #2E7D32)' : 'linear-gradient(135deg, #B71C1C, #C62828)',
              p: 3, color: '#fff',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Gavel />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>이의제기 심사</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)' }}>
                    이의제기 #{selected?.id}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3, pt: 3 }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#555', mb: 1 }}>심사 결과</Typography>
              <ToggleButtonGroup
                value={reviewResult}
                exclusive
                onChange={(_, v) => { if (v) setReviewResult(v); }}
                fullWidth
                sx={{ borderRadius: '12px' }}
              >
                <ToggleButton value="ACCEPTED"
                  sx={{
                    borderRadius: '10px !important', textTransform: 'none', fontWeight: 700,
                    '&.Mui-selected': { background: '#2E7D32', color: '#fff', '&:hover': { background: '#1B5E20' } },
                  }}>
                  <CheckCircle sx={{ fontSize: 18, mr: 0.8 }} /> 인정
                </ToggleButton>
                <ToggleButton value="REJECTED"
                  sx={{
                    borderRadius: '10px !important', textTransform: 'none', fontWeight: 700, ml: 1,
                    '&.Mui-selected': { background: '#C62828', color: '#fff', '&:hover': { background: '#B71C1C' } },
                  }}>
                  <Cancel sx={{ fontSize: 18, mr: 0.8 }} /> 기각
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField
              fullWidth multiline rows={4}
              label="심사 사유 (필수)"
              value={reviewReason}
              onChange={e => setReviewReason(e.target.value)}
              placeholder="심사 결과에 대한 사유를 작성해주세요..."
              sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
            {reviewResult === 'ACCEPTED' && (
              <TextField
                fullWidth
                label="보상 내용 (선택)"
                value={compensation}
                onChange={e => setCompensation(e.target.value)}
                placeholder="인정 시 제공할 보상 내용 (예: 정지 해제, 하트 지급 등)"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button onClick={() => { setReviewDialog(false); setReviewReason(''); setCompensation(''); }} sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}>취소</Button>
            <Button
              variant="contained"
              disabled={actionLoading || !reviewReason.trim()}
              onClick={handleReview}
              sx={{
                borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3,
                background: reviewResult === 'ACCEPTED' ? '#2E7D32' : '#C62828',
                '&:hover': { background: reviewResult === 'ACCEPTED' ? '#1B5E20' : '#B71C1C' },
              }}
              startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : reviewResult === 'ACCEPTED' ? <CheckCircle /> : <Cancel />}
            >
              {reviewResult === 'ACCEPTED' ? '인정 확정' : '기각 확정'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '12px' }}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default AdminAppealsPage;
